import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import ApiClient from '../../services/apiClient/ApiClient'
import type {
  LandFavorite,
  CreateLandFavoriteRequest,
  BuildingFavorite,
  CreateBuildingFavoriteRequest,
} from '@popety-io/types'

interface EnhancedFavoritesState {
  landFavorites: LandFavorite[]
  buildingFavorites: BuildingFavorite[]
  loading: boolean
  error: string | null
}

const apiClient = new ApiClient()

// Land Favorites Async Thunks
export const getAllLandFavorites = createAsyncThunk(
  'enhancedFavorites/getAllLandFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/favorites/land')
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch land favorites' })
    }
  }
)

export const createLandFavorite = createAsyncThunk(
  'enhancedFavorites/createLandFavorite',
  async (favoriteData: CreateLandFavoriteRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/favorites/land', favoriteData)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite
    } catch {
      return rejectWithValue({ message: 'Failed to create land favorite' })
    }
  }
)

export const deleteLandFavorite = createAsyncThunk(
  'enhancedFavorites/deleteLandFavorite',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/favorites/land/${id}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return id
    } catch {
      return rejectWithValue({ message: 'Failed to delete land favorite' })
    }
  }
)

// Building Favorites Async Thunks
export const getAllBuildingFavorites = createAsyncThunk(
  'enhancedFavorites/getAllBuildingFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/favorites/building')
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as BuildingFavorite[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch building favorites' })
    }
  }
)

export const createBuildingFavorite = createAsyncThunk(
  'enhancedFavorites/createBuildingFavorite',
  async (favoriteData: CreateBuildingFavoriteRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/favorites/building', favoriteData)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as BuildingFavorite
    } catch {
      return rejectWithValue({ message: 'Failed to create building favorite' })
    }
  }
)

export const deleteBuildingFavorite = createAsyncThunk(
  'enhancedFavorites/deleteBuildingFavorite',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/favorites/building/${id}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return id
    } catch {
      return rejectWithValue({ message: 'Failed to delete building favorite' })
    }
  }
)

const initialState: EnhancedFavoritesState = {
  landFavorites: [],
  buildingFavorites: [],
  loading: false,
  error: null,
}

const enhancedFavoritesSlice = createSlice({
  name: 'enhancedFavorites',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearAllFavorites: (state) => {
      state.landFavorites = []
      state.buildingFavorites = []
    },
    clearLandFavorites: (state) => {
      state.landFavorites = []
    },
    clearBuildingFavorites: (state) => {
      state.buildingFavorites = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Land Favorites
      .addCase(getAllLandFavorites.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAllLandFavorites.fulfilled,
        (state, action: PayloadAction<LandFavorite[]>) => {
          state.loading = false
          state.landFavorites = action.payload
        }
      )
      .addCase(getAllLandFavorites.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to fetch land favorites'
      })
      .addCase(createLandFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createLandFavorite.fulfilled,
        (state, action: PayloadAction<LandFavorite>) => {
          state.loading = false
          state.landFavorites.push(action.payload)
        }
      )
      .addCase(createLandFavorite.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to create land favorite'
      })
      .addCase(deleteLandFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteLandFavorite.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.landFavorites = state.landFavorites.filter(
            (fav) => fav.id !== action.payload
          )
        }
      )
      .addCase(deleteLandFavorite.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to delete land favorite'
      })

      // Building Favorites
      .addCase(getAllBuildingFavorites.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAllBuildingFavorites.fulfilled,
        (state, action: PayloadAction<BuildingFavorite[]>) => {
          state.loading = false
          state.buildingFavorites = action.payload
        }
      )
      .addCase(getAllBuildingFavorites.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to fetch building favorites'
      })
      .addCase(createBuildingFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createBuildingFavorite.fulfilled,
        (state, action: PayloadAction<BuildingFavorite>) => {
          state.loading = false
          state.buildingFavorites.push(action.payload)
        }
      )
      .addCase(createBuildingFavorite.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to create building favorite'
      })
      .addCase(deleteBuildingFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteBuildingFavorite.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.buildingFavorites = state.buildingFavorites.filter(
            (fav) => fav.id !== action.payload
          )
        }
      )
      .addCase(deleteBuildingFavorite.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to delete building favorite'
      })
  },
})

export const {
  clearError,
  clearAllFavorites,
  clearLandFavorites,
  clearBuildingFavorites,
} = enhancedFavoritesSlice.actions

export const enhancedFavoritesReducer = {
  enhancedFavorites: enhancedFavoritesSlice.reducer,
}
